import { isArray } from '../array.js';
import type * as Core from '../../types/calculation-core.js';
export function success<T>(value: T, metadata: Core.CoreMetadata): Extract<Core.Calculation<T>, {
    ok: true;
}> {
    return { ok: true, value, metadata };
}
export function failure(errors: readonly Core.InputError[], metadata: Core.CoreMetadata): Extract<Core.Calculation<never>, {
    ok: false;
}> {
    return { ok: false, errors, metadata };
}
export function isFiniteNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value);
}
function isIsoDate(value: unknown) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const timestamp = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value;
}
function isHttpsUrl(value: unknown) {
    if (typeof value !== 'string')
        return false;
    const UrlConstructor = globalThis.URL;
    if (UrlConstructor === undefined)
        return false;
    try {
        return new UrlConstructor(value).protocol === 'https:';
    }
    catch {
        return false;
    }
}
export function validateMetadata(metadata: Core.CoreMetadata): Core.InputError[] {
    const errors = [];
    if (!metadata || typeof metadata !== 'object') {
        return [{ field: 'config.metadata', code: 'INVALID_CONFIG', message: 'Kural metadata kaydı geçersiz.' }];
    }
    if (typeof metadata.calculatorId !== 'string' || metadata.calculatorId.trim() === '') {
        errors.push({ field: 'config.metadata.calculatorId', code: 'INVALID_CONFIG', message: 'Hesap kimliği boş olamaz.' });
    }
    if (typeof metadata.version !== 'string' || metadata.version.trim() === '') {
        errors.push({ field: 'config.metadata.version', code: 'INVALID_CONFIG', message: 'Kural sürümü boş olamaz.' });
    }
    if (!isIsoDate(metadata.effectiveFrom)) {
        errors.push({ field: 'config.metadata.effectiveFrom', code: 'INVALID_CONFIG', message: 'Yürürlük tarihi YYYY-AA-GG biçiminde olmalıdır.' });
    }
    if (metadata.effectiveTo !== undefined && !isIsoDate(metadata.effectiveTo)) {
        errors.push({ field: 'config.metadata.effectiveTo', code: 'INVALID_CONFIG', message: 'Bitiş tarihi YYYY-AA-GG biçiminde olmalıdır.' });
    }
    if (!isIsoDate(metadata.lastVerifiedAt)) {
        errors.push({ field: 'config.metadata.lastVerifiedAt', code: 'INVALID_CONFIG', message: 'Kaynak kontrol tarihi YYYY-AA-GG biçiminde olmalıdır.' });
    }
    if (!isArray(metadata.sources)) {
        errors.push({ field: 'config.metadata.sources', code: 'INVALID_CONFIG', message: 'Kaynak listesi dizi olmalıdır.' });
    }
    else {
        metadata.sources.forEach((source: Core.CoreMetadata["sources"][number], index) => {
            const field = `config.metadata.sources[${index}]`;
            if (!source || typeof source !== 'object') {
                errors.push({ field, code: 'INVALID_CONFIG', message: 'Kaynak kaydı geçersiz.' });
                return;
            }
            if (typeof source.id !== 'string' || source.id.trim() === '') {
                errors.push({ field: `${field}.id`, code: 'INVALID_CONFIG', message: 'Kaynak kimliği boş olamaz.' });
            }
            if (typeof source.title !== 'string' || source.title.trim() === '') {
                errors.push({ field: `${field}.title`, code: 'INVALID_CONFIG', message: 'Kaynak başlığı boş olamaz.' });
            }
            if (typeof source.publisher !== 'string' || source.publisher.trim() === '') {
                errors.push({ field: `${field}.publisher`, code: 'INVALID_CONFIG', message: 'Kaynak yayıncısı boş olamaz.' });
            }
            if (!isHttpsUrl(source.url)) {
                errors.push({ field: `${field}.url`, code: 'INVALID_CONFIG', message: 'Kaynak URL HTTPS olmalıdır.' });
            }
            if (source.verifiedAt !== undefined && !isIsoDate(source.verifiedAt)) {
                errors.push({ field: `${field}.verifiedAt`, code: 'INVALID_CONFIG', message: 'Kaynak kontrol tarihi YYYY-AA-GG biçiminde olmalıdır.' });
            }
        });
    }
    return errors;
}
export function resolveMetadata(candidate: Core.CoreMetadata | undefined, fallback: Core.CoreMetadata): Core.CoreMetadata {
    if (!candidate || typeof candidate !== 'object')
        return fallback;
    const metadata = candidate;
    return validateMetadata(metadata).length === 0 ? metadata : fallback;
}
